import React, { useState, useEffect, useRef } from "react";
import {
	Box,
	Typography,
	Grid,
	Button,
	Card,
	CardContent,
	IconButton,
	Menu,
	MenuItem,
	Chip,
	Skeleton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import EastIcon from "@mui/icons-material/East";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

/**
 * Tarjeta de guía en el DASHBOARD y en la página "Mis guías" (MyGuidesPage).
 */
const MyGuideCardDashboard = ({
	guide,
	onOpenGuide,
	onViewGuide,
	onRequestDeleteGuide,
}) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);

	const handleMenuClick = (event) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const { id, name, updatedAt, pageCount = 0 } = guide;

	const formatDate = (timestamp) => {
		if (timestamp == null || timestamp === "") return "";
		const date = new Date(
			typeof timestamp === "number" && timestamp < 1e10
				? timestamp * 1000
				: timestamp,
		);
		if (Number.isNaN(date.getTime())) return "";
		const now = new Date();
		const diff = now - date;
		const minutes = Math.floor(diff / (1000 * 60));
		const hours = Math.floor(diff / (1000 * 60 * 60));
		const days = Math.floor(diff / (1000 * 60 * 60 * 24));

		if (minutes < 1) return "Ahora mismo";
		if (minutes < 60) return `Hace ${minutes} min`;
		if (hours < 24) return `Hace ${hours}h`;
		if (days === 1) return "Ayer";
		if (days < 7) return `Hace ${days} días`;
		if (days < 30) return `Hace ${Math.floor(days / 7)} sem`;

		return date.toLocaleDateString("es-ES", {
			day: "numeric",
			month: "short",
		});
	};

	const getGradient = (guideName, guideId) => {
		const gradients = [
			"linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
			"linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
			"linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
			"linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
			"linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
			"linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
			"linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
			"linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)",
		];
		const seed = String(guideName || "") + String(guideId ?? "");
		const index = seed
			? [...seed].reduce((acc, c) => acc + c.charCodeAt(0), 0) %
				gradients.length
			: 0;
		return gradients[index];
	};

	const formattedDate = formatDate(updatedAt);
	const gradient = getGradient(name, id);

	return (
		<Card
			elevation={0}
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				borderRadius: "20px",
				border: "1px solid rgba(0, 0, 0, 0.06)",
				overflow: "hidden",
				cursor: "default",
				transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
				backgroundColor: "#fff",
				"&:hover": {
					transform: "translateY(-6px)",
					boxShadow: "0 16px 40px rgba(99, 102, 241, 0.15)",
					borderColor: "rgba(99, 102, 241, 0.2)",
					"& .guide-avatar": {
						transform: "scale(1.08)",
					},
				},
			}}
		>
			{/* Gradient accent bar */}
			<Box
				sx={{
					height: 5,
					background: gradient,
					flexShrink: 0,
					borderRadius: "20px 20px 0 0",
				}}
			/>

			<CardContent
				sx={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					p: 2.5,
				}}
			>
				{/* Header: avatar + menu */}
				<Box
					sx={{
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "space-between",
						mb: 2,
					}}
				>
					<Box
						className="guide-avatar"
						sx={{
							width: 52,
							height: 52,
							borderRadius: "16px",
							background: gradient,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
							boxShadow: "0 4px 14px rgba(0, 0, 0, 0.12)",
							transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
						}}
					>
						<Typography
							sx={{
								fontSize: "1.4rem",
								fontWeight: 800,
								color: "rgba(255, 255, 255, 0.95)",
								textTransform: "uppercase",
								lineHeight: 1,
							}}
						>
							{name?.charAt(0) || "G"}
						</Typography>
					</Box>

					<IconButton
						size="small"
						onClick={handleMenuClick}
						sx={{
							backgroundColor: "rgba(0, 0, 0, 0.04)",
							width: 32,
							height: 32,
							"&:hover": {
								backgroundColor: "rgba(0, 0, 0, 0.08)",
							},
						}}
					>
						<MoreHorizIcon sx={{ fontSize: 18, color: "#64748B" }} />
					</IconButton>
				</Box>

				{/* Title */}
				<Typography
					variant="h6"
					sx={{
						fontWeight: 700,
						color: "#1a1a2e",
						fontSize: "1.05rem",
						lineHeight: 1.35,
						mb: 1.5,
						display: "-webkit-box",
						WebkitLineClamp: 2,
						WebkitBoxOrient: "vertical",
						overflow: "hidden",
					}}
				>
					{name || "Sin título"}
				</Typography>

				{/* Metadata row */}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						flexWrap: "wrap",
					}}
				>
					{pageCount > 0 && (
						<Chip
							label={`${pageCount} ${pageCount === 1 ? "página" : "páginas"}`}
							size="small"
							sx={{
								height: 24,
								fontSize: "0.75rem",
								fontWeight: 600,
								backgroundColor: "rgba(99, 102, 241, 0.08)",
								color: "#6366F1",
								border: "none",
							}}
						/>
					)}
					{formattedDate && (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								color: "#94a3b8",
								fontSize: "0.8rem",
							}}
						>
							<AccessTimeIcon sx={{ fontSize: 14 }} />
							{formattedDate}
						</Box>
					)}
				</Box>

				<Box sx={{ flexGrow: 1 }} />

				{/* Action buttons — side by side */}
				<Box
					sx={{
						display: "flex",
						gap: 1.5,
						mt: 2,
						pt: 2,
						borderTop: "1px solid rgba(0, 0, 0, 0.06)",
					}}
				>
					{onViewGuide && (
						<Button
							variant="outlined"
							fullWidth
							size="small"
							startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 17 }} />}
							onClick={(e) => {
								e.stopPropagation();
								onViewGuide(id);
							}}
							sx={{
								textTransform: "none",
								fontWeight: 600,
								fontSize: "0.838rem",
								borderRadius: "10px",
								py: 0.9,
								borderColor: "rgba(99, 102, 241, 0.4)",
								color: "#6366F1",
								"&:hover": {
									borderColor: "#6366F1",
									backgroundColor: "rgba(99, 102, 241, 0.08)",
								},
							}}
						>
							Ver
						</Button>
					)}
					<Button
						variant="contained"
						fullWidth
						size="small"
						startIcon={<EditOutlinedIcon sx={{ fontSize: 17 }} />}
						onClick={(e) => {
							e.stopPropagation();
							onOpenGuide?.(id);
						}}
						sx={{
							textTransform: "none",
							fontWeight: 600,
							fontSize: "0.838rem",
							borderRadius: "10px",
							py: 0.9,
							backgroundColor: "#6366F1 !important",
							color: "#fff !important",
							boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)",
							"&:hover": {
								backgroundColor: "#4F46E5 !important",
								boxShadow: "0 4px 12px rgba(99, 102, 241, 0.4)",
							},
						}}
					>
						Editar
					</Button>
				</Box>
			</CardContent>

			{/* Context Menu */}
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleMenuClose}
				onClick={(e) => e.stopPropagation()}
				PaperProps={{
					sx: {
						minWidth: 200,
						borderRadius: "14px",
						boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
						border: "1px solid rgba(0, 0, 0, 0.06)",
						mt: 1,
					},
				}}
			>
				<MenuItem
					onClick={() => {
						handleMenuClose();
						onOpenGuide?.(id);
					}}
					sx={{ py: 1.25, px: 2, mx: 1, borderRadius: "8px" }}
				>
					<EditOutlinedIcon sx={{ mr: 1.5, fontSize: 18, color: "#64748b" }} />
					<Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
						Editar
					</Typography>
				</MenuItem>
				<MenuItem
					onClick={() => {
						handleMenuClose();
						onRequestDeleteGuide?.(guide);
					}}
					sx={{
						py: 1.25,
						px: 2,
						mx: 1,
						borderRadius: "8px",
						"&:hover": {
							backgroundColor: "rgba(239, 68, 68, 0.08)",
						},
					}}
				>
					<DeleteOutlineIcon sx={{ mr: 1.5, fontSize: 18, color: "#EF4444" }} />
					<Typography
						sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#EF4444" }}
					>
						Eliminar
					</Typography>
				</MenuItem>
			</Menu>
		</Card>
	);
};

const MyGuidesSection = ({
	guides = [],
	loading = false,
	loadingMore = false,
	hasMore = false,
	onLoadMore,
	onOpenGuide,
	onViewGuide,
	onDeleteGuide,
	onCreateNew,
	onSeeAll,
	maxGuides,
}) => {
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [guideToDelete, setGuideToDelete] = useState(null);
	const loadMoreSentinelRef = useRef(null);

	const guidesToShow = maxGuides != null ? guides.slice(0, maxGuides) : guides;
	const isLimited = maxGuides != null && guides.length > maxGuides;

	// Scroll infinito: cargar más cuando el sentinel entra en viewport
	useEffect(() => {
		if (!hasMore || !onLoadMore || loading || loadingMore) return;
		const el = loadMoreSentinelRef.current;
		if (!el) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) onLoadMore();
			},
			{ rootMargin: "200px", threshold: 0.1 },
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [hasMore, onLoadMore, loading, loadingMore]);

	const handleRequestDelete = (guide) => {
		setGuideToDelete(guide);
		setDeleteConfirmOpen(true);
	};

	const handleConfirmDelete = async () => {
		if (guideToDelete?.id) {
			setDeleteConfirmOpen(false);
			setGuideToDelete(null);
			await onDeleteGuide?.(guideToDelete.id);
		}
	};

	const handleCloseDeleteConfirm = () => {
		setGuideToDelete(null);
		setDeleteConfirmOpen(false);
	};

	return (
		<Box sx={{ mb: 5 }}>
			{/* Section Header */}
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-end",
					mb: 3,
					flexWrap: "wrap",
					gap: 2,
				}}
			>
				<Box>
					<Typography
						variant="h5"
						sx={{
							fontWeight: 700,
							color: "#1a1a2e",
							mb: 0.5,
							fontSize: { xs: "1.25rem", md: "1.5rem" },
							letterSpacing: "-0.02em",
						}}
					>
						Mis guías
					</Typography>
					<Typography
						sx={{
							color: "#64748B",
							fontSize: "0.938rem",
						}}
					>
						Continúa donde lo dejaste
					</Typography>
				</Box>

				{guides.length > 0 && (isLimited || (hasMore && onSeeAll)) && (
					<Button
						variant="text"
						endIcon={<EastIcon />}
						onClick={onSeeAll}
						sx={{
							textTransform: "none",
							fontWeight: 600,
							color: "#6366F1",
							border: "none !important",
							borderWidth: "0 !important",
							outline: "none",
							boxShadow: "none",
							fontSize: "0.875rem",
							"&:hover": {
								backgroundColor: "rgba(99, 102, 241, 0.08)",
							},
						}}
					>
						Ver todas
					</Button>
				)}
			</Box>

			{/* Content */}
			{loading ? (
				<Grid container spacing={3}>
					{Array.from({ length: 3 }).map((_, index) => (
						<Grid item xs={12} sm={6} lg={4} key={index}>
							<Card
								elevation={0}
								sx={{
									borderRadius: "20px",
									overflow: "hidden",
									border: "1px solid rgba(0, 0, 0, 0.06)",
								}}
							>
								<Skeleton
									variant="rectangular"
									height={160}
									sx={{ bgcolor: "rgba(0, 0, 0, 0.06)" }}
								/>
								<CardContent sx={{ p: 2.5 }}>
									<Skeleton
										variant="text"
										width="70%"
										height={24}
										sx={{ mb: 1 }}
									/>
									<Skeleton variant="text" width="40%" height={20} />
								</CardContent>
							</Card>
						</Grid>
					))}
				</Grid>
			) : guidesToShow.length === 0 ? (
				/* Empty State */
				<Box
					sx={{
						textAlign: "center",
						py: 8,
						px: 4,
						background:
							"linear-gradient(135deg, rgba(99, 102, 241, 0.03) 0%, rgba(168, 85, 247, 0.03) 100%)",
						borderRadius: "24px",
						border: "2px dashed rgba(99, 102, 241, 0.2)",
					}}
				>
					<Box
						sx={{
							width: 100,
							height: 100,
							borderRadius: "24px",
							background:
								"linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(168, 85, 247, 0.15) 100%)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							mx: "auto",
							mb: 3,
						}}
					>
						<FolderOpenOutlinedIcon sx={{ fontSize: 48, color: "#6366F1" }} />
					</Box>
					<Typography
						variant="h6"
						sx={{
							color: "#1a1a2e",
							fontWeight: 700,
							mb: 1,
							fontSize: "1.25rem",
						}}
					>
						Tu espacio de trabajo está vacío
					</Typography>
					<Typography
						sx={{
							color: "#64748B",
							mb: 4,
							maxWidth: 400,
							mx: "auto",
							fontSize: "0.938rem",
							lineHeight: 1.6,
						}}
					>
						Crea tu primera guía de viaje y empieza a planificar tu próxima
						aventura
					</Typography>
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={onCreateNew}
						sx={{
							textTransform: "none",
							fontWeight: 600,
							background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
							px: 4,
							py: 1.5,
							borderRadius: "14px",
							fontSize: "0.938rem",
							boxShadow: "0 4px 20px rgba(99, 102, 241, 0.4)",
							"&:hover": {
								background: "linear-gradient(135deg, #5558E3 0%, #7C4DE8 100%)",
								boxShadow: "0 6px 28px rgba(99, 102, 241, 0.5)",
							},
						}}
					>
						Crear mi primera guía
					</Button>
				</Box>
			) : (
				<Grid container spacing={3}>
					{/* Create New Card */}
					<Grid item xs={12} sm={6} lg={4}>
						<Card
							onClick={onCreateNew}
							elevation={0}
							sx={{
								height: "100%",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								borderRadius: "20px",
								paddingTop: { xs: 2, sm: 0 },
								paddingBottom: { xs: 2, sm: 0 },
								border: "2px dashed rgba(99, 102, 241, 0.3)",
								background:
									"linear-gradient(135deg, rgba(99, 102, 241, 0.03) 0%, rgba(168, 85, 247, 0.03) 100%)",
								cursor: "pointer",
								transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
								"&:hover": {
									borderColor: "#6366F1",
									background:
										"linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%)",
									transform: "translateY(-4px)",
									"& .add-icon-wrapper": {
										transform: "scale(1.1)",
										background:
											"linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
										"& svg": {
											color: "#fff",
										},
									},
								},
							}}
						>
							<Box
								className="add-icon-wrapper"
								sx={{
									width: 72,
									height: 72,
									borderRadius: "18px",
									backgroundColor: "rgba(99, 102, 241, 0.1)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									mb: 2,
									transition: "all 0.3s ease",
								}}
							>
								<AddIcon
									sx={{
										fontSize: 36,
										color: "#6366F1",
										transition: "color 0.3s ease",
									}}
								/>
							</Box>
							<Typography
								sx={{
									fontWeight: 700,
									color: "#1a1a2e",
									fontSize: "1rem",
									mb: 0.5,
								}}
							>
								Nueva guía
							</Typography>
							<Typography
								sx={{
									color: "#64748B",
									fontSize: "0.875rem",
								}}
							>
								Empieza desde cero
							</Typography>
						</Card>
					</Grid>

					{/* User's Guides */}
					{guidesToShow.map((guide) => (
						<Grid item xs={12} sm={6} lg={4} key={guide.id}>
							<MyGuideCardDashboard
								guide={guide}
								onOpenGuide={onOpenGuide}
								onViewGuide={onViewGuide}
								onRequestDeleteGuide={handleRequestDelete}
							/>
						</Grid>
					))}

					{/* Sentinel para scroll infinito */}
					{hasMore && onLoadMore && (
						<Grid
							item
							xs={12}
							sx={{ minHeight: 1, py: 2 }}
							ref={loadMoreSentinelRef}
						>
							{loadingMore && (
								<Grid container spacing={3}>
									{Array.from({ length: 3 }).map((_, idx) => (
										<Grid item xs={12} sm={6} lg={4} key={idx}>
											<Card
												elevation={0}
												sx={{
													borderRadius: "20px",
													overflow: "hidden",
													border: "1px solid rgba(0, 0, 0, 0.06)",
												}}
											>
												<Skeleton
													variant="rectangular"
													height={160}
													sx={{ bgcolor: "rgba(0, 0, 0, 0.06)" }}
												/>
												<CardContent sx={{ p: 2.5 }}>
													<Skeleton
														variant="text"
														width="70%"
														height={24}
														sx={{ mb: 1 }}
													/>
													<Skeleton variant="text" width="40%" height={20} />
												</CardContent>
											</Card>
										</Grid>
									))}
								</Grid>
							)}
						</Grid>
					)}
				</Grid>
			)}

			{/* Modal de confirmación para eliminar guía */}
			<Dialog
				open={deleteConfirmOpen}
				onClose={handleCloseDeleteConfirm}
				PaperProps={{
					sx: {
						borderRadius: "20px",
						padding: 1,
						maxWidth: 400,
					},
				}}
			>
				<DialogTitle
					sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#1a1a2e" }}
				>
					¿Eliminar esta guía?
				</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ color: "#64748B", fontSize: "0.938rem" }}>
						{guideToDelete?.name ? (
							<>
								La guía &quot;{guideToDelete.name}&quot; se eliminará
								permanentemente. Esta acción no se puede deshacer.
							</>
						) : (
							<>
								Esta acción no se puede deshacer. Perderás todo el contenido de
								la guía permanentemente.
							</>
						)}
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, pt: 0, gap: 1 }}>
					<Button
						onClick={handleCloseDeleteConfirm}
						sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
					>
						Cancelar
					</Button>
					<Button
						variant="contained"
						onClick={handleConfirmDelete}
						startIcon={<DeleteOutlineIcon />}
						sx={{
							textTransform: "none",
							fontWeight: 600,
							borderRadius: 2,
							backgroundColor: "#ef4444",
							boxShadow: "0 2px 8px rgba(239,68,68,0.3)",
							"&:hover": {
								backgroundColor: "#dc2626",
								boxShadow: "0 4px 12px rgba(239,68,68,0.4)",
							},
						}}
					>
						Eliminar
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default MyGuidesSection;
